/**
 * Image processing utilities for frontend
 */

export interface ImageProcessingOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // For JPEG compression (0-1)
  outputFormat?: 'jpeg' | 'png' | 'webp';
}

export interface ProcessedImageData {
  base64: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  originalSize: number;
  compressed: boolean;
}

/**
 * Convert file to base64 with optional compression
 */
export async function processImageFile(
  file: File,
  options: ImageProcessingOptions = {}
): Promise<ProcessedImageData> {
  const {
    maxWidth = 2048,
    maxHeight = 2048,
    quality = 0.9,
    outputFormat = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    img.onload = () => {
      try {
        let { width, height } = img;
        let compressed = false;

        // Calculate new dimensions if resizing is needed
        if (width > maxWidth || height > maxHeight) {
          const aspectRatio = width / height;
          
          if (width > height) {
            width = Math.min(width, maxWidth);
            height = width / aspectRatio;
          } else {
            height = Math.min(height, maxHeight);
            width = height * aspectRatio;
          }
          
          compressed = true;
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64
        const mimeType = `image/${outputFormat}`;
        const dataURL = canvas.toDataURL(mimeType, quality);
        
        // Extract base64 data (remove data URL prefix)
        const base64Data = dataURL.split(',')[1];
        
        // Calculate approximate file size from base64
        const approximateSize = Math.round((base64Data.length * 3) / 4);
        
        // Create processed file name
        const originalExtension = file.name.split('.').pop() || '';
        const nameWithoutExt = file.name.replace(`.${originalExtension}`, '');
        const processedFileName = compressed 
          ? `${nameWithoutExt}_processed.${outputFormat}`
          : `${nameWithoutExt}.${outputFormat}`;

        resolve({
          base64: base64Data,
          fileName: processedFileName,
          fileType: mimeType,
          fileSize: approximateSize,
          originalSize: file.size,
          compressed
        });
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Simple file to base64 conversion without processing
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Get optimal processing options based on file size
 */
export function getOptimalProcessingOptions(fileSize: number): ImageProcessingOptions {
  // For files larger than 5MB, apply more aggressive compression
  if (fileSize > 5 * 1024 * 1024) {
    return {
      maxWidth: 1920,
      maxHeight: 1920,
      quality: 0.8,
      outputFormat: 'jpeg'
    };
  }
  
  // For files larger than 2MB, apply moderate compression
  if (fileSize > 2 * 1024 * 1024) {
    return {
      maxWidth: 2048,
      maxHeight: 2048,
      quality: 0.85,
      outputFormat: 'jpeg'
    };
  }
  
  // For smaller files, minimal processing
  return {
    maxWidth: 2048,
    maxHeight: 2048,
    quality: 0.9,
    outputFormat: 'jpeg'
  };
}

/**
 * Validate image file
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: ${allowedTypes.map(t => t.split('/')[1]).join(', ')}`
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${maxSize / 1024 / 1024}MB`
    };
  }

  return { valid: true };
}